/* eslint-disable max-lines */
import { cloneDeep, isEmpty } from 'lodash-es';

import {
  FieldKinds,
  FormViews,
  PathService,
  RegisterViewColumnsSetting,
} from '@site-mate/dashpivot-shared-library';

import { FormService } from 'app/form/form.service';
import { FormFieldHeader } from 'app/form-fields/common/register/form-field-header/form-field-header';
import { FormFieldHeaderFactory } from 'app/form-fields/form-field-header-factory';
import { FormFieldRegisterFactory } from 'app/form-fields/form-field-register-factory';
import { FormWeb } from 'app/shared/model/form.model';
import { TemplateWeb } from 'app/shared/model/template.model';

import { FormsRegisterColumn } from './forms-register-column';
import { FormsRegisterStaticFieldFactory } from './forms-register-static-fields/forms-register-static-field-factory';
import { StaticFieldIds } from './forms-register-static-fields/static-field-ids.enum';

type ColumnId = string;
type FormId = string;
type FormWithFoldersIds = FormWeb & {
  template?: {
    team?: { _id: string };
    project?: { _id: string };
  };
};

export class FormsRegister {
  private _isEmpty = true;
  public get isEmpty() {
    return this._isEmpty;
  }

  private reachedEnd = false;
  private offset = 0;
  private _columns: FormsRegisterColumn[];
  private readonly columnsMap: Map<ColumnId, FormsRegisterColumn> = new Map();

  constructor(
    private readonly template: TemplateWeb & { path?: string },
    private readonly formService: FormService,
  ) {
    this.buildHeaders();
  }

  private getMetaData(form: FormWithFoldersIds): Record<string, string> {
    const { project, team } = form.template || {};

    if (project && team) {
      return { project: project._id, team: team._id };
    }

    const [, projectId, teamId] = new PathService().extractFolderIds(this.template.path);
    return { project: projectId, team: teamId };
  }

  public get columns(): FormsRegisterColumn[] {
    if (!this._columns) {
      const spreadedColumns = [];

      // We need to "spread" the columns that have multiple columns in them
      this.columnsMap.forEach((column) => {
        const columnNeedsToBeSpread = column.header.columns.length > 1;

        if (!columnNeedsToBeSpread) {
          spreadedColumns.push(column);
        }

        if (columnNeedsToBeSpread) {
          spreadedColumns.push(...this.spreadColumn(column));
        }
      });

      this._columns = spreadedColumns;
    }

    return this._columns;
  }

  public get filterParams(): Record<string, unknown> {
    const params: Record<string, unknown> = {};

    this.columns.forEach(({ header }) => {
      if (header.filter) {
        const filterParams = header.filter.queryParams();
        if (!isEmpty(filterParams)) {
          params[header.id] = JSON.stringify(filterParams);
        }
      }
    });

    return params;
  }

  public get filterCount(): number {
    let filterCount = 0;
    const columnsWithFilters = this.columns.filter((column) => column.header.filter);
    const filters = columnsWithFilters.map(({ header }) => header.filter);

    const countedFilters = new Set<string>();
    filters.forEach((filter) => {
      if (!countedFilters.has(filter.uniqueId)) {
        filterCount += filter.status;
        countedFilters.add(filter.uniqueId);
      }
    });

    return filterCount;
  }

  public async fetchRows(rowCount: number, additionalParams: Record<string, string> = {}): Promise<void> {
    if (this.reachedEnd) {
      return;
    }

    const limit = rowCount;
    const params = { ...additionalParams, ...this.getURLSearchParams(limit), view: FormViews.Register };
    this.offset += limit;
    const forms = await this.formService.listTemplateForms(this.template.id, params).toPromise();
    this.addFormsToColumnsMap(forms);

    if (forms.length) {
      this._isEmpty = false;
    }

    const loadedAllForms = forms.length !== rowCount;
    if (loadedAllForms) {
      this.reachedEnd = true;
    }
  }

  public setFilterMap(filterMap: Map<ColumnId, any>) {
    this.columns.forEach(({ header }) => {
      header.filter?.set(filterMap.get(header.id));
    });
  }

  public removeForms(formIds: FormId[]) {
    formIds.forEach((formId) => {
      this.columnsMap.forEach((column) => {
        column.removeField(formId);
      });
    });

    const [firstColumn] = this.columnsMap.values();
    if (firstColumn.fieldsMap.size === 0) {
      this._isEmpty = true;
    }
    this._columns = null;
  }

  private spreadColumn(column: FormsRegisterColumn) {
    const spreadedColumns = [];
    // columnHeaderColumn :) - is because the header for this column has columns
    column.header.columns.forEach((columnHeaderColumn, index) => {
      const fieldsMap = new Map();

      column.fieldsMap.forEach((field, formId) => {
        fieldsMap.set(formId, {
          ...field,
          grid: field.grid.map((cells) => [cells[index]]),
        });
      });

      const subcolumn = new FormsRegisterColumn(cloneDeep(column.header));
      subcolumn.header.filter = column.header.filter;
      subcolumn.header.columns = [columnHeaderColumn];

      subcolumn.fieldsMap = fieldsMap;

      spreadedColumns.push(subcolumn);
    });

    return spreadedColumns;
  }

  private addFormsToColumnsMap(forms: any[]) {
    forms.forEach((form: FormWithFoldersIds) => {
      this.columnsMap.forEach((column, key) => {
        return column.header.static
          ? this.createStaticColumns(form, column)
          : this.createDynamicColumns(form, column, key);
      });
    });
    this._columns = null;
  }

  private createStaticColumns(form: FormWithFoldersIds, column: FormsRegisterColumn): void {
    const kind = column.header.id as StaticFieldIds;
    const item = FormsRegisterStaticFieldFactory.getStaticFormField({ form, kind, template: this.template });
    const metadata = this.getMetaData(form);

    const field = FormFieldRegisterFactory.getFormField(item, column.header, metadata);

    column.addField({ id: form._id, field });
  }

  private createDynamicColumns(form: FormWithFoldersIds, column: FormsRegisterColumn, key: string): void {
    const formItem = form.items.find((item) => item._id === key);
    const metadata = this.getMetaData(form);

    const field = FormFieldRegisterFactory.getFormField(formItem || {}, column.header, metadata);

    column.addField({ id: form._id, field });
  }

  private buildHeaders() {
    this.buildStaticHeaders();
    this.buildFieldHeaders();
  }

  private buildStaticHeaders(): void {
    const staticHeaders: FormFieldHeader[] = [];
    const columnSettings =
      this.template.registerViewColumnsSetting?.metadata || ({} as RegisterViewColumnsSetting['metadata']);
    const noSettings = isEmpty(columnSettings);

    const includeProjectHeader =
      this.template.isOrganisationTemplate && (noSettings || columnSettings.showProject);
    if (includeProjectHeader) {
      staticHeaders.push(
        FormFieldHeaderFactory.getHeader({
          id: StaticFieldIds.Project,
          description: 'Project',
          kind: 'category',
          static: true,
        }),
      );
    }

    const includeTeamHeader = this.template.isOrganisationTemplate && (noSettings || columnSettings.showTeam);
    if (includeTeamHeader) {
      staticHeaders.push(
        FormFieldHeaderFactory.getHeader({ id: 'team', description: 'Team', kind: 'category', static: true }),
      );
    }

    const includeCreatedByHeader = noSettings || columnSettings.showCreatedBy;
    if (includeCreatedByHeader) {
      staticHeaders.push(
        FormFieldHeaderFactory.getHeader({
          id: StaticFieldIds.CreatedBy,
          description: 'Created by',
          kind: 'user',
          static: true,
        }),
      );
    }

    const includeCreatedAtHeader = noSettings || columnSettings.showCreatedOn;
    if (includeCreatedAtHeader) {
      staticHeaders.push(
        FormFieldHeaderFactory.getHeader({
          id: StaticFieldIds.CreatedAt,
          description: 'Created Date',
          kind: 'date',
          static: true,
        }),
      );
    }

    const includeLastUpdatedAtHeader = noSettings || columnSettings.showLastUpdatedOn;
    if (includeLastUpdatedAtHeader) {
      staticHeaders.push(
        FormFieldHeaderFactory.getHeader({
          id: StaticFieldIds.UpdatedAt,
          description: 'Last Updated',
          kind: 'date',
          static: true,
        }),
      );
    }

    const includeFormIdHeader = noSettings || columnSettings.showFormId;
    if (includeFormIdHeader) {
      staticHeaders.push(
        FormFieldHeaderFactory.getHeader({
          id: StaticFieldIds.Id,
          description: 'Form ID',
          static: true,
        }),
      );
    }

    const includeFormNumberHeader =
      noSettings || (this.template.counterIsActivated && columnSettings.showFormCounter);
    if (includeFormNumberHeader) {
      staticHeaders.push(
        FormFieldHeaderFactory.getHeader({
          id: StaticFieldIds.FormNumber,
          description: 'Form Number',
          static: true,
        }),
      );
    }

    const includeFormVersionHeader = noSettings || columnSettings.showDocVersion;
    if (includeFormVersionHeader) {
      staticHeaders.push(
        FormFieldHeaderFactory.getHeader({
          id: StaticFieldIds.FormVersion,
          description: 'Form version',
          static: true,
        }),
      );
    }

    const includeWorkflowColumnHeader = noSettings || columnSettings.showWorkFlowStep;
    if (includeWorkflowColumnHeader) {
      staticHeaders.push(
        FormFieldHeaderFactory.getHeader({
          id: StaticFieldIds.WorkflowStep,
          description: 'Workflow column',
          kind: 'workflowStep',
          static: true,
        }),
      );
    }

    staticHeaders.forEach((header) => {
      this.columnsMap.set(header.id, new FormsRegisterColumn(header));
    });
  }

  private getURLSearchParams(limit: number) {
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.set('limit', String(limit));
    searchParams.set('skip', String(this.offset));

    return Array.from(searchParams.entries()).reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});
  }

  private buildFieldHeaders(): void {
    const mapOfRegisterFields = new Map();
    const templateFields = this.template.items;
    const templateFieldsInOrder = [];

    const columnsInSettings = this.template.registerViewColumnsSetting?.fields || [];

    templateFields.forEach((field) => {
      mapOfRegisterFields.set(field._id, field);
    });

    columnsInSettings
      .filter((item) => item.visible)
      .forEach((column) => {
        templateFieldsInOrder.push(mapOfRegisterFields.get(column.itemId));
      });

    templateFieldsInOrder
      .filter(({ kind }) => kind !== FieldKinds.PageBreak)
      .map((field) => ({ ...field, templateId: this.template.id }))
      .forEach((field) => {
        const header = FormFieldHeaderFactory.getHeader(field);
        this.columnsMap.set(header.id, new FormsRegisterColumn(header));
      });
  }
}
